import { useEffect, useRef, useState, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../../api/client";
import PortalLayout from "../layout/PortalLayout";
import { AuthContext } from "../../auth/AuthContext";
import { usePortalData } from "../context/PortalDataContext";
import { Toast } from "primereact/toast";

// ── Constants ─────────────────────────────────────────────────────────────────
const ACCEPTED_TYPES  = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const MAX_MB          = 10;
const MAX_IMAGE_DIM   = 1920;   // max width or height after resize
const JPEG_QUALITY    = 0.78;   // 78% — sharp enough for text, much smaller file

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function formatFileSize(bytes) {
  if (!bytes || bytes < 1024)   return `${bytes ?? 0} B`;
  if (bytes < 1024 * 1024)      return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const STATUS_MAP = {
  pending_review: { cls: "warning", label: "Pending review", icon: "pi-clock"       },
  processed:      { cls: "success", label: "Processed",      icon: "pi-check-circle" },
  rejected:       { cls: "danger",  label: "Rejected",       icon: "pi-times-circle" },
};

/**
 * Compress an image File using a canvas.
 * - Resizes to MAX_IMAGE_DIM on the longest side
 * - Re-encodes as JPEG at JPEG_QUALITY
 * - Returns a new File with the same name
 */
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img    = new Image();
    const reader = new FileReader();

    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;

    img.onload = () => {
      let { width, height } = img;

      // Scale down if either dimension exceeds MAX_IMAGE_DIM
      if (width > MAX_IMAGE_DIM || height > MAX_IMAGE_DIM) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_IMAGE_DIM);
          width  = MAX_IMAGE_DIM;
        } else {
          width  = Math.round((width / height) * MAX_IMAGE_DIM);
          height = MAX_IMAGE_DIM;
        }
      }

      const canvas    = document.createElement("canvas");
      canvas.width    = width;
      canvas.height   = height;
      const ctx       = canvas.getContext("2d");

      // White background (important for PNGs with transparency)
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error("Canvas compression failed")); return; }
          // Keep original name but ensure .jpg extension
          const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
          resolve(new File([blob], name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    };

    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── WebcamCapture modal (desktop only) ───────────────────────────────────────
function WebcamCapture({ onCapture, onClose }) {
  const videoRef   = useRef(null);
  const streamRef  = useRef(null);
  const [ready, setReady]   = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().then(() => setReady(true)).catch(() => {});
          };
        }
      })
      .catch((err) => {
        setError(err.name === "NotAllowedError"
          ? "Camera access was denied. Please allow camera permission in your browser."
          : "Could not access camera: " + err.message);
      });
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const capture = () => {
    const video  = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onCapture(file);
    }, "image/jpeg", 0.92);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1200,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#000", borderRadius: 16, overflow: "hidden",
          width: "100%", maxWidth: 640,
          boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "12px 16px", background: "rgba(255,255,255,0.08)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="pi pi-camera" style={{ color: "var(--brand-primary)", fontSize: 14 }} />
            <span style={{ fontWeight: 600, fontSize: 14, color: "#fff" }}>Take a photo</span>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)",
            background: "rgba(255,255,255,0.1)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 12,
          }}>
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Video area */}
        <div style={{ position: "relative", background: "#111", minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {error ? (
            <div style={{ padding: 24, textAlign: "center", color: "#f87171" }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: 28, marginBottom: 12, display: "block" }} />
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>{error}</div>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: "100%", display: "block", maxHeight: 420, objectFit: "cover" }}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px", background: "rgba(255,255,255,0.05)",
          display: "flex", justifyContent: "center",
        }}>
          <button
            onClick={capture}
            disabled={!ready}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 28px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              background: ready ? "var(--brand-primary)" : "#555", color: "white",
              border: "none", cursor: ready ? "pointer" : "not-allowed", fontFamily: "inherit",
            }}
          >
            <i className="pi pi-camera" style={{ fontSize: 14 }} />
            Capture
          </button>
        </div>
      </div>
    </div>
  );
}

// ── FileViewer modal ──────────────────────────────────────────────────────────
function FileViewer({ upload, onClose, onResend }) {
  if (!upload) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface-card)", borderRadius: 16,
          overflow: "hidden", maxWidth: 860, width: "100%",
          maxHeight: "90vh", display: "flex", flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "12px 14px",
          borderBottom: "1px solid var(--border-color)",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {/* Row 1: file info + close button */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <i
              className={`pi ${upload.file_type === "pdf" ? "pi-file-pdf" : "pi-image"}`}
              style={{ color: "var(--brand-primary)", fontSize: 15, flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontWeight: 600, fontSize: 14, color: "var(--text-primary)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {upload.original_name}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 1 }}>
                {formatDate(upload.created_at)}
              </div>
            </div>
            {/* Close — always top-right */}
            <button
              onClick={onClose}
              style={{
                flexShrink: 0, width: 32, height: 32, borderRadius: 8,
                border: "1px solid var(--border-color)", background: "var(--surface-bg)",
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 13, color: "var(--text-secondary)",
              }}
            >
              <i className="pi pi-times" />
            </button>
          </div>

          {/* Row 2: action buttons — always fully visible */}
          <div style={{ display: "flex", gap: 8 }}>
            {onResend && (
              <button
                onClick={() => { onClose(); onResend(upload); }}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
                  background: "var(--brand-primary)", color: "white",
                  border: "none", cursor: "pointer", fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="pi pi-send" style={{ fontSize: 11 }} />
                Resend to Pharmacy
              </button>
            )}
            <a
              href={upload.file_url}
              target="_blank"
              rel="noreferrer"
              style={{
                flex: onResend ? "0 0 auto" : 1,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
                background: "var(--brand-light)", color: "var(--brand-primary)",
                textDecoration: "none", border: "1px solid var(--brand-accent)",
                whiteSpace: "nowrap",
              }}
            >
              <i className="pi pi-external-link" style={{ fontSize: 11 }} />
              Open full
            </a>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", background: "#1a1a2e", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
          {upload.file_type === "pdf" ? (
            <iframe
              src={upload.file_url}
              title="Prescription PDF"
              style={{ width: "100%", height: "75vh", border: "none" }}
            />
          ) : (
            <img
              src={upload.file_url}
              alt="Prescription"
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", display: "block" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── ResendModal ───────────────────────────────────────────────────────────────
function ResendModal({ upload, onClose, onConfirm, resendingId }) {
  const [note, setNote] = useState("");
  if (!upload) return null;
  const isSending = resendingId === upload.id;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface-card)", borderRadius: 16,
          width: "100%", maxWidth: 440,
          boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid var(--border-color)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <i className="pi pi-send" style={{ color: "var(--brand-primary)", fontSize: 14 }} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Resend to Pharmacy</span>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 8, border: "1px solid var(--border-color)",
            background: "var(--surface-bg)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, color: "var(--text-secondary)",
          }}>
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Upload summary */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            background: "var(--surface-bg)", borderRadius: 10, padding: "10px 14px",
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 8, flexShrink: 0,
              background: upload.file_type === "pdf" ? "#fef2f2" : "#eff6ff",
              color: upload.file_type === "pdf" ? "#dc2626" : "#1d4ed8",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, overflow: "hidden", border: "1px solid var(--border-color)",
            }}>
              {upload.file_type !== "pdf" && upload.file_url
                ? <img src={upload.file_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <i className={`pi ${upload.file_type === "pdf" ? "pi-file-pdf" : "pi-image"}`} />
              }
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {upload.original_name}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>
                Uploaded {formatDate(upload.created_at)}
              </div>
            </div>
          </div>

          {/* Required message */}
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", display: "block", marginBottom: 4 }}>
              Message to pharmacy
              <span style={{ color: "#dc2626", marginLeft: 3 }}>*</span>
            </label>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
              Please include the number of days requested and any other instructions for the pharmacist.
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. 30 days supply, urgent, specific brand preferred…"
              style={{
                width: "100%", fontSize: 13, padding: "10px 12px",
                border: "1px solid var(--border-color)", borderRadius: 10,
                fontFamily: "inherit", color: "var(--text-primary)",
                background: "var(--surface-card)", resize: "vertical",
                outline: "none", boxSizing: "border-box", lineHeight: 1.6,
              }}
              onFocus={(e) => e.target.style.borderColor = "var(--brand-primary)"}
              onBlur={(e)  => e.target.style.borderColor = "var(--border-color)"}
            />
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} disabled={isSending} style={{
              padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500,
              background: "none", border: "1px solid var(--border-color)",
              color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit",
              opacity: isSending ? 0.5 : 1,
            }}>
              Cancel
            </button>
            <button onClick={() => onConfirm(note)} disabled={isSending || !note.trim()} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
              background: "var(--brand-primary)", color: "white", border: "none",
              cursor: (isSending || !note.trim()) ? "not-allowed" : "pointer", fontFamily: "inherit",
              opacity: (isSending || !note.trim()) ? 0.6 : 1, transition: "opacity 0.15s",
            }}>
              <i className={`pi ${isSending ? "pi-spin pi-spinner" : "pi-send"}`} style={{ fontSize: 12 }} />
              {isSending ? "Sending…" : "Send to Pharmacy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── RecentUploadsStrip ────────────────────────────────────────────────────────
function RecentUploadsStrip({ uploads, loading, onView, onResend, resendingId }) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className="ph-card" style={{ padding: "14px 16px" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.4px", textTransform: "uppercase", marginBottom: 10 }}>
          Previous Uploads
        </div>
        {[1, 2].map(i => (
          <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border-light)" }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--border-light)", flexShrink: 0, animation: "pulse 1.4s ease-in-out infinite" }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, justifyContent: "center" }}>
              <div style={{ height: 12, width: "50%", borderRadius: 4, background: "var(--border-light)", animation: "pulse 1.4s ease-in-out infinite" }} />
              <div style={{ height: 10, width: "30%", borderRadius: 4, background: "var(--border-light)", animation: "pulse 1.4s ease-in-out infinite" }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!uploads.length) return null;

  const visible = expanded ? uploads : uploads.slice(0, 3);

  return (
    <div className="ph-card" style={{ padding: 0, overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", borderBottom: "1px solid var(--border-light)",
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.4px", textTransform: "uppercase" }}>
          Previous Uploads
        </div>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {uploads.length} upload{uploads.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Rows */}
      {visible.map((u, idx) => {
        const s = STATUS_MAP[u.status] ?? STATUS_MAP.pending_review;
        const isSending = resendingId === u.id;
        const isLast = idx === visible.length - 1;

        return (
          <div key={u.id} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "11px 16px",
            borderBottom: isLast ? "none" : "1px solid var(--border-light)",
          }}>
            {/* Thumbnail */}
            <div
              onClick={() => u.file_url && onView(u)}
              title={u.file_url ? "Click to view" : undefined}
              style={{
                width: 42, height: 42, borderRadius: 9, flexShrink: 0,
                background: u.file_type === "pdf" ? "#fef2f2" : "#eff6ff",
                color: u.file_type === "pdf" ? "#dc2626" : "#1d4ed8",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 17, cursor: u.file_url ? "pointer" : "default",
                overflow: "hidden", border: "1px solid var(--border-color)",
                transition: "transform 0.15s",
              }}
              onMouseEnter={(e) => { if (u.file_url) e.currentTarget.style.transform = "scale(1.06)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              {u.file_type !== "pdf" && u.file_url
                ? <img src={u.file_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <i className={`pi ${u.file_type === "pdf" ? "pi-file-pdf" : "pi-image"}`} />
              }
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u.original_name}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{formatDate(u.created_at)}</span>
                <span className={`ph-tag ${s.cls}`} style={{ fontSize: 10.5 }}>
                  <i className={`pi ${s.icon}`} style={{ fontSize: 9 }} />
                  {s.label}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
              {u.file_url && (
                <button onClick={() => onView(u)} style={{
                  background: "none", border: "1px solid var(--border-color)", cursor: "pointer",
                  borderRadius: 8, padding: "5px 10px", fontSize: 12, color: "var(--text-secondary)",
                  display: "flex", alignItems: "center", gap: 4, fontFamily: "inherit",
                }}>
                  <i className="pi pi-eye" style={{ fontSize: 11 }} />
                  View
                </button>
              )}
              <button onClick={() => onResend(u)} disabled={isSending} style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: "var(--brand-primary)", color: "white", border: "none",
                cursor: isSending ? "not-allowed" : "pointer", fontFamily: "inherit",
                opacity: isSending ? 0.7 : 1,
              }}>
                <i className={`pi ${isSending ? "pi-spin pi-spinner" : "pi-send"}`} style={{ fontSize: 11 }} />
                {isSending ? "Sending…" : "Resend"}
              </button>
            </div>
          </div>
        );
      })}

      {/* Show more / less */}
      {uploads.length > 3 && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            width: "100%", padding: "10px", fontSize: 12.5, fontWeight: 500,
            background: "var(--surface-bg)", border: "none", borderTop: "1px solid var(--border-light)",
            cursor: "pointer", color: "var(--brand-primary)", fontFamily: "inherit",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
        >
          <i className={`pi ${expanded ? "pi-chevron-up" : "pi-chevron-down"}`} style={{ fontSize: 11 }} />
          {expanded ? "Show less" : `Show ${uploads.length - 3} more`}
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function UploadPrescription() {
  const { orgId }   = useContext(AuthContext);
  const { refresh } = usePortalData();
  const toast       = useRef(null);
  const fileInputRef = useRef(null);
  const cameraRef    = useRef(null);

  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const relativeId      = searchParams.get("relative_id");
  const relativeName    = searchParams.get("name");
  const filterUploadId  = searchParams.get("filter_upload");
  const relativeParam   = relativeId ? `?relative_id=${relativeId}` : "";

  const [uploads, setUploads]           = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [viewingUpload, setViewingUpload]   = useState(null);   // FileViewer state
  const [resendTarget, setResendTarget]     = useState(null);   // upload being resent
  const [resendingId, setResendingId]       = useState(null);   // id currently in-flight

  // Upload form state
  const [file, setFile]             = useState(null);     // compressed File
  const [originalSize, setOriginalSize] = useState(null); // before compression
  const [preview, setPreview]       = useState(null);     // data URL
  const [compressing, setCompressing] = useState(false);
  const [notes, setNotes]           = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging]     = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);

  // ── History ───────────────────────────────────────────────────────────
  const loadHistory = () => {
    if (!orgId) { setHistoryLoading(false); return; }
    setHistoryLoading(true);
    api.get(`prescription-uploads/${relativeParam}`)
      .then(res => {
        const fetched = res.data ?? [];
        setUploads(fetched);
        // Auto-open FileViewer when arriving from a notification
        if (filterUploadId) {
          const target = fetched.find(u => u.id === filterUploadId);
          if (target) setViewingUpload(target);
        }
      })
      .catch(err => console.error("Upload history error", err))
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => { loadHistory(); }, [orgId, relativeId]);

  // ── File handling + compression ───────────────────────────────────────
  const handleFile = async (incoming) => {
    if (!incoming) return;

    if (!ACCEPTED_TYPES.includes(incoming.type)) {
      toast.current.show({
        severity: "error", summary: "Invalid file type",
        detail: "Please upload a JPG, PNG, WEBP, or PDF file.", life: 4000,
      });
      return;
    }

    if (incoming.size > MAX_MB * 1024 * 1024) {
      toast.current.show({
        severity: "error", summary: "File too large",
        detail: `Maximum file size is ${MAX_MB}MB.`, life: 4000,
      });
      return;
    }

    setOriginalSize(incoming.size);

    // PDFs — skip compression, just set directly
    if (incoming.type === "application/pdf") {
      setFile(incoming);
      setPreview(null);
      return;
    }

    // Images — compress via canvas
    setCompressing(true);
    try {
      const compressed = await compressImage(incoming);
      setFile(compressed);
      // Generate preview from compressed file
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(compressed);
    } catch (err) {
      console.error("Compression failed, falling back to original", err);
      // Fallback to original if compression fails
      setFile(incoming);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(incoming);
    } finally {
      setCompressing(false);
    }
  };

  // ── Drag and drop ─────────────────────────────────────────────────────
  const onDragOver  = (e) => { e.preventDefault(); setDragging(true);  };
  const onDragLeave = (e) => { e.preventDefault(); setDragging(false); };
  const onDrop      = (e) => {
    e.preventDefault(); setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const submit = async () => {
    if (!file) {
      toast.current.show({ severity: "warn", summary: "No file selected", detail: "Please select or photograph your prescription.", life: 3000 });
      return;
    }
    if (!orgId) {
      toast.current.show({ severity: "error", summary: "No pharmacy selected", detail: "Please select a pharmacy first.", life: 3000 });
      return;
    }
    if (!notes.trim()) {
      toast.current.show({ severity: "warn", summary: "Notes required", detail: "Please add notes for the pharmacy, including the number of days requested.", life: 4000 });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file",  file);
      formData.append("notes", notes.trim());
      if (relativeId) formData.append("relative_id", relativeId);

      await api.post("prescription-uploads/upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.current.show({
        severity: "success", summary: "Uploaded",
        detail: "Your prescription has been sent to the pharmacy for review.",
        life: 5000,
      });

      setFile(null); setPreview(null); setNotes(""); setOriginalSize(null);
      loadHistory();
      refresh();
    } catch (err) {
      const detail = err.response?.data?.error ?? err.response?.data?.detail ?? "Upload failed. Please try again.";
      toast.current.show({ severity: "error", summary: "Upload failed", detail, life: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const clearFile = () => { setFile(null); setPreview(null); setOriginalSize(null); };

  const openResend = (upload) => {
    setResendTarget(upload);
  };

  const submitResend = async (note = "") => {
    if (!resendTarget) return;
    setResendingId(resendTarget.id);
    try {
      await api.post(`prescription-uploads/${resendTarget.id}/resend/`, {
        notes: note.trim(),
        ...(relativeId && { relative_id: relativeId }),
      });
      toast.current.show({
        severity: "success", summary: "Resent",
        detail: "Your prescription has been resent to the pharmacy.",
        life: 4000,
      });
      setResendTarget(null);
      loadHistory();
      refresh();
    } catch (err) {
      const detail = err.response?.data?.error ?? err.response?.data?.detail ?? "Resend failed. Please try again.";
      toast.current.show({ severity: "error", summary: "Resend failed", detail, life: 5000 });
    } finally {
      setResendingId(null);
    }
  };

  const savedBytes  = originalSize && file ? Math.max(0, originalSize - file.size) : 0;
  const showSavings = savedBytes > 50 * 1024; // only show if saved >50KB

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <PortalLayout>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
      <Toast ref={toast} position="top-center" />

      {viewingUpload && (
        <FileViewer upload={viewingUpload} onClose={() => setViewingUpload(null)} onResend={openResend} />
      )}

      {resendTarget && (
        <ResendModal
          upload={resendTarget}
          onClose={() => setResendTarget(null)}
          onConfirm={(note) => submitResend(note)}
          resendingId={resendingId}
        />
      )}

      {webcamOpen && (
        <WebcamCapture
          onCapture={(capturedFile) => { setWebcamOpen(false); handleFile(capturedFile); }}
          onClose={() => setWebcamOpen(false)}
        />
      )}

      {relativeId && relativeName && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "#eff6ff", border: "1px solid #bfdbfe",
          borderRadius: 10, padding: "10px 16px", marginBottom: 16,
          fontSize: 13, color: "#1e40af",
        }}>
          <i className="pi pi-users" style={{ fontSize: 14, flexShrink: 0 }} />
          <span>Uploading on behalf of <b>{relativeName}</b></span>
        </div>
      )}

      <div className="page-header">
        <h1 className="page-title">
          {relativeId ? `Upload for ${relativeName}` : "Upload Prescription"}
        </h1>
        <p className="page-subtitle">Send a photo of your prescription to your pharmacy for review and arrange</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 640 }}>

        {/* ── Upload form ────────────────────────────────────────────── */}

          {/* Notification filter banner */}
          {filterUploadId && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "var(--brand-light)", border: "1px solid var(--brand-accent)",
              borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--brand-primary)" }}>
                <i className="pi pi-filter" style={{ fontSize: 12 }} />
                <span style={{ fontWeight: 500 }}>Showing linked prescription upload</span>
              </div>
              <button
                onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.delete("filter_upload");
                  navigate(`/upload?${p.toString()}`, { replace: true });
                  setViewingUpload(null);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: "none", border: "1px solid var(--brand-accent)",
                  borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 500,
                  color: "var(--brand-primary)", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <i className="pi pi-times" style={{ fontSize: 10 }} /> Dismiss
              </button>
            </div>
          )}

          {/* Recent uploads strip — encourage reuse */}
          {(historyLoading || uploads.length > 0) && (
            <RecentUploadsStrip
              uploads={uploads}
              loading={historyLoading}
              onView={setViewingUpload}
              onResend={openResend}
              resendingId={resendingId}
            />
          )}

          {/* Drop zone OR file preview */}
          {!file && !compressing ? (
            <div
              onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? "var(--brand-primary)" : "var(--border-color)"}`,
                borderRadius: 14,
                background: dragging ? "var(--brand-light)" : "var(--surface-card)",
                padding: "48px 24px", textAlign: "center", cursor: "pointer",
                transition: "all 0.18s",
              }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: 16, background: "var(--brand-light)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
              }}>
                <i className="pi pi-upload" style={{ fontSize: 22, color: "var(--brand-primary)" }} />
              </div>
              <div style={{ fontWeight: 600, fontSize: 15, color: "var(--text-primary)", marginBottom: 6 }}>
                Drop your prescription here
              </div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>
                or click to browse · JPG, PNG, PDF up to {MAX_MB}MB
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: "var(--brand-primary)", color: "white", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                  <i className="pi pi-folder-open" style={{ fontSize: 13 }} /> Browse files
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isMobile) {
                      cameraRef.current?.click();
                    } else {
                      setWebcamOpen(true);
                    }
                  }}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500, background: "var(--surface-bg)", color: "var(--text-secondary)", border: "1px solid var(--border-color)", cursor: "pointer", fontFamily: "inherit" }}>
                  <i className="pi pi-camera" style={{ fontSize: 13 }} /> Take photo
                </button>
              </div>
              {/* Browse: no capture attribute — goes to gallery/files on mobile, file dialog on desktop */}
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files[0])} />
              {/* Camera: capture="environment" used only on mobile via button logic above */}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files[0])} />
            </div>

          ) : compressing ? (
            /* Compressing indicator */
            <div style={{
              border: "2px dashed var(--border-color)", borderRadius: 14,
              background: "var(--surface-card)", padding: "48px 24px", textAlign: "center",
            }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, color: "var(--brand-primary)", display: "block", marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)" }}>Optimising image…</div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>Compressing for faster upload</div>
            </div>

          ) : (
            /* File preview card */
            <div className="ph-card" style={{ overflow: "hidden" }}>
              {/* Image preview */}
              {preview && (
                <div style={{ position: "relative", maxHeight: 280, overflow: "hidden" }}>
                  <img src={preview} alt="Prescription preview" style={{ width: "100%", objectFit: "cover", display: "block" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.25))" }} />
                </div>
              )}

              {/* PDF banner */}
              {!preview && (
                <div style={{ background: "#fef2f2", padding: "28px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="pi pi-file-pdf" style={{ fontSize: 22, color: "#dc2626" }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>{file.name}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>PDF · {formatFileSize(file.size)}</div>
                  </div>
                </div>
              )}

              {/* File info bar */}
              <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-light)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{file.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 1, display: "flex", alignItems: "center", gap: 8 }}>
                    <span>{formatFileSize(file.size)}</span>
                    {/* Compression savings badge */}
                    {showSavings && (
                      <span style={{ background: "#ecfdf5", color: "#059669", borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>
                        <i className="pi pi-arrow-down" style={{ fontSize: 9, marginRight: 3 }} />
                        {formatFileSize(savedBytes)} saved
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={clearFile} style={{ background: "none", border: "1px solid var(--border-color)", borderRadius: 8, padding: "5px 12px", fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit" }}>
                  Change
                </button>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="ph-card" style={{ padding: "16px 20px" }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", display: "block", marginBottom: 4 }}>
              Notes to pharmacy
              <span style={{ color: "#dc2626", marginLeft: 3 }}>*</span>
            </label>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
              Please include the number of days requested and any other instructions for the pharmacist.
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. 30 days supply, urgent, specific brand preferred…"
              style={{
                width: "100%", fontSize: 13, padding: "10px 12px",
                border: "1px solid var(--border-color)", borderRadius: 10,
                fontFamily: "inherit", color: "var(--text-primary)",
                background: "var(--surface-card)", resize: "vertical",
                outline: "none", boxSizing: "border-box", lineHeight: 1.6,
              }}
              onFocus={(e) => e.target.style.borderColor = "var(--brand-primary)"}
              onBlur={(e)  => e.target.style.borderColor = "var(--border-color)"}
            />
          </div>

          {/* Submit */}
          <button
            onClick={submit}
            disabled={submitting || !file || compressing || !notes.trim()}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              width: "100%", padding: "13px 20px", borderRadius: 12,
              fontSize: 14, fontWeight: 600, fontFamily: "inherit",
              background: "var(--brand-primary)", color: "white", border: "none",
              cursor: (submitting || !file || compressing || !notes.trim()) ? "not-allowed" : "pointer",
              opacity: (submitting || !file || compressing || !notes.trim()) ? 0.6 : 1,
              transition: "opacity 0.15s",
            }}
          >
            <i className={`pi ${submitting ? "pi-spin pi-spinner" : "pi-send"}`} style={{ fontSize: 14 }} />
            {submitting ? "Sending to pharmacy…" : "Send to Pharmacy"}
          </button>

          {/* What happens next */}
          <div style={{ background: "var(--surface-bg)", borderRadius: 10, padding: "14px 16px", fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.7 }}>
            <div style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>What happens next?</div>
            {[
              { icon: "pi-send",         text: "Your prescription is sent to the pharmacy" },
              { icon: "pi-eye",          text: "A pharmacist reviews it and prepares your medicine" },
              { icon: "pi-check-circle", text: "You can then collect or get it delivered once notified" },
            ].map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 4 }}>
                <i className={`pi ${step.icon}`} style={{ fontSize: 12, color: "var(--brand-primary)", marginTop: 2, flexShrink: 0 }} />
                <span>{step.text}</span>
              </div>
            ))}
          </div>
        </div>
    </PortalLayout>
  );
}